/**
 * License Intelligence Optimization Findings Mock Data
 *
 * Mock data for the findings API endpoint.
 * In production, this data will come from:
 * GET /ECMv6/api/sodms/license-intelligence/v1/findings?scope=sap&page=0&size=10
 */

import type { OptimizationFinding } from '../components/Details/Overview/OptimizationFindings/type';

interface FindingsPage {
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface FindingsMockData {
  findings: OptimizationFinding[];
  netAnnualImpact: number;
  netAnnualImpactDisplayValue: string;
  page: FindingsPage;
}

interface FindingsResponse {
  data: FindingsMockData;
  status: number;
  message: string;
}

export const LICENSE_INTELLIGENCE_FINDINGS_DATA: FindingsResponse = {
  data: {
    netAnnualImpact: 48960,
    netAnnualImpactDisplayValue: '$144K',
    findings: [
      {
        id: 'DEPROVISION_DORMANT_ADVANCED_7',
        title: 'Deprovision 14 dormant Advanced-tier users',
        savings: 13440,
        savingsDisplayValue: '$13,440',
        category: 'Cost Reduction',
        impact: 'High',
        effort: 'Low',
        confidencePct: 95,
        vendorAttribution: 'SAP',
        endpointKey: 7,
        accountCount: 14,
        currentTier: 'Advanced',
        recommendedTier: null,
        reasonText:
          '14 dormant accounts consuming Advanced tier FUE (1.0 each); all accounts inactive 90+ days; 14 FUE × $960/FUE = $13,440/year recoverable.',
      },
      {
        id: 'TIER_DOWNGRADE_Advanced_Core_7',
        title: 'Downgrade 200 users from Advanced to Core',
        savings: 153600,
        savingsDisplayValue: '$154K',
        category: 'License Change',
        impact: 'High',
        effort: 'Low',
        confidencePct: 85,
        vendorAttribution: 'SAP',
        endpointKey: 7,
        accountCount: 200,
        currentTier: 'Advanced',
        recommendedTier: 'Core',
        reasonText:
          'STAR simulation shows actual usage matches Core (0.2) instead of Advanced (1.0). 200 accounts × $768 savings = $153,600/year.',
      },
      {
        id: 'TIER_DOWNGRADE_Advanced_SelfService_7',
        title: 'Downgrade 45 users from Advanced to Self-Service',
        savings: 41760,
        savingsDisplayValue: '$42K',
        category: 'License Change',
        impact: 'Medium',
        effort: 'Low',
        confidencePct: 80,
        vendorAttribution: 'SAP',
        endpointKey: 7,
        accountCount: 45,
        currentTier: 'Advanced',
        recommendedTier: 'Self-Service',
        reasonText:
          'STAR simulation shows actual usage matches Self-Service (0.033) instead of Advanced (1.0). 45 accounts × $928 savings = $41,760/year.',
      },
      {
        id: 'ROLE_RESTRUCTURE_Z_FI_GL_MANAGER_7',
        title: 'Restructure role Z_FI_GL_MANAGER to reduce tier requirement',
        savings: 7680,
        savingsDisplayValue: '$7,680',
        category: 'Role Optimization',
        impact: 'Medium',
        effort: 'Medium',
        confidencePct: 72,
        vendorAttribution: 'SAP',
        endpointKey: 7,
        accountCount: 10,
        currentTier: 'Advanced',
        recommendedTier: 'Core',
        reasonText:
          'Role Z_FI_GL_MANAGER assigns Advanced tier but 10 users only use Core-level transactions. Restructuring saves (1.0 - 0.2) × 10 × $960 = $7,680/year.',
      },
      {
        id: 'DEPROVISION_DORMANT_CORE_7',
        title: 'Deprovision 8 dormant Core-tier users',
        savings: 1536,
        savingsDisplayValue: '$1,536',
        category: 'Cost Reduction',
        impact: 'Low',
        effort: 'Low',
        confidencePct: 95,
        vendorAttribution: 'SAP',
        endpointKey: 7,
        accountCount: 8,
        currentTier: 'Core',
        recommendedTier: null,
        reasonText:
          '8 dormant accounts consuming Core tier FUE (0.2 each); all accounts inactive 90+ days; 8 × 0.2 FUE × $960/FUE = $1,536/year recoverable.',
      },
    ],
    page: { number: 0, size: 10, totalElements: 5, totalPages: 1 },
  },
  status: 200,
  message: 'Findings retrieved successfully',
};

export default LICENSE_INTELLIGENCE_FINDINGS_DATA;
